import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/apiClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FreeWebsiteAudit() {
  const [form, setForm] = useState({
    name: "", company_name: "", website: "", email: "", phone: "",
    niche: "", city: "", state: "", customer_goals: "", biggest_concern: "",
    hp_field: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.company_name.trim() || !form.email.trim() || !form.website.trim()) {
      toast.error("Name, company, email and website are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/audit-request`, form);
      setDone(true);
      toast.success("Request received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="free-audit-page"
      className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden"
    >
      {/* Simple header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="font-display text-2xl tracking-tight leading-none text-white">
              <span className="italic">A</span>
              <span className="italic -ml-1.5">S</span>
            </span>
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/60 border-l border-white/15 pl-3 hidden sm:inline">
              Almonte Sites
            </span>
          </Link>
          <Link to="/" className="text-xs text-white/50 hover:text-white transition-colors">
            ← Back to site
          </Link>
        </div>
      </header>

      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[540px] h-[540px] rounded-full bg-blue-500/10 blur-[130px]" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          {!done ? (
            <>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/5">
                <ShieldCheck size={13} className="text-blue-400" />
                <span className="text-[11px] tracking-[0.22em] uppercase text-blue-300 font-medium">
                  Free · No commitment
                </span>
              </span>
              <h1
                data-testid="audit-headline"
                className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold leading-[1.05] mt-6"
              >
                Find out where your website may be{" "}
                <span className="italic">losing potential customers</span>.
              </h1>
              <p
                data-testid="audit-subheadline"
                className="mt-6 text-white/65 text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
              >
                Request a free personalized website audit from Almonte Sites. We&apos;ll review your website and identify practical opportunities to improve its design, credibility, mobile experience, and ability to turn visitors into inquiries.
              </p>
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5">
                <CheckCircle2 size={13} className="text-emerald-400" />
                <span className="text-[11px] tracking-[0.22em] uppercase text-emerald-300 font-medium">
                  Request received
                </span>
              </span>
              <h1
                data-testid="audit-confirmation"
                className="font-display text-4xl sm:text-5xl tracking-tight font-semibold leading-[1.05] mt-6"
              >
                Thanks — your audit request is in.
              </h1>
              <p className="mt-6 text-white/65 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
                Elijah will personally review your website and reply within 24–48 hours with practical, specific feedback. No pressure, no auto-generated fluff.
              </p>
              <Link
                to="/"
                className="inline-flex mt-8 items-center gap-2 text-sm rounded-full border border-white/15 hover:border-white/30 px-5 py-2.5 text-white/80 hover:text-white transition-colors"
              >
                Back to Almonte Sites
              </Link>
            </>
          )}
        </div>
      </section>

      {!done && (
        <section className="pb-24">
          <div className="max-w-3xl mx-auto px-6">
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-7 md:p-10"
              data-testid="audit-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F l="Name *"><Input value={form.name} onChange={update("name")} data-testid="audit-name" placeholder="Your full name" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" /></F>
                <F l="Company name *"><Input value={form.company_name} onChange={update("company_name")} data-testid="audit-company" placeholder="e.g. GlassCoat Detailing" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" /></F>
                <F l="Website URL *" full><Input value={form.website} onChange={update("website")} data-testid="audit-website" placeholder="https://yourbusiness.com" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" /></F>
                <F l="Email *"><Input type="email" value={form.email} onChange={update("email")} data-testid="audit-email" placeholder="you@business.com" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" /></F>
                <F l="Phone"><Input value={form.phone} onChange={update("phone")} data-testid="audit-phone" placeholder="(555) 555-5555" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" /></F>
                <F l="Business niche"><Input value={form.niche} onChange={update("niche")} data-testid="audit-niche" placeholder="Roofing, MMA Gym, Landscaping…" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" /></F>
                <F l="City & state" full>
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={form.city} onChange={update("city")} data-testid="audit-city" placeholder="City" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" />
                    <Input value={form.state} onChange={update("state")} data-testid="audit-state" placeholder="State" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35 h-12" />
                  </div>
                </F>
                <F l="Type of customers / projects you want more of" full>
                  <Textarea rows={3} value={form.customer_goals} onChange={update("customer_goals")} data-testid="audit-goals" placeholder="e.g. Homeowners in Miami booking roof estimates" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35" />
                </F>
                <F l="Biggest website concern" full>
                  <Textarea rows={3} value={form.biggest_concern} onChange={update("biggest_concern")} data-testid="audit-concern" placeholder="e.g. My site looks outdated and no one is filling out the contact form" className="bg-[#0A0A0A] border-white/15 text-white placeholder:text-white/35" />
                </F>
              </div>

              {/* Honeypot field — hidden from real users */}
              <input
                type="text"
                tabIndex="-1"
                autoComplete="off"
                value={form.hp_field}
                onChange={update("hp_field")}
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
              />

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-xs text-white/40 max-w-sm">
                  I&apos;ll never sell your info. Response within 24–48 hours. No promises of guaranteed leads or revenue — just honest, specific feedback.
                </p>
                <Button type="submit" disabled={submitting} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-12 px-7 font-medium shadow-[0_10px_40px_-10px_rgba(59,130,246,0.6)] disabled:opacity-60" data-testid="audit-submit">
                  {submitting ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <>Request my free audit <ArrowUpRight size={16} className="ml-1.5" /></>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}

function F({ l, children, full }) {
  return (
    <div className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs uppercase tracking-[0.18em] text-white/55 font-medium">{l}</Label>
      {children}
    </div>
  );
}
